<template>
    <div class="w-100">
        <div class="d-flex align-center mb-10">
            <div class="title mr-3">{{ $t('Vacations / absence') }}</div>
            <div v-if="flextimeCompanies.length > 1" style="max-width: 300px">
                <dropdown
                    :items="flextimeCompanies"
                    :selected="flextimeCompanies.filter(x => x.id === selectedCompanyId)"
                    name-key="name"
                    value-key="id"
                    show-selected-first
                    :label="$t('Company')"
                    @input="onSelectCompanyAccount"
                />
            </div>
            <div v-else-if="flextimeCompanies.find(x => x.id === selectedCompanyId)" class="body-2">
                {{ flextimeCompanies.find(x => x.id === selectedCompanyId).name }}
            </div>
            <v-spacer/>
            <div class="d-flex align-end">
                <div class="mr-10">
                    <div class="g4--text caption">{{ $t('Flextime bonus') }}</div>
                    <div class="headline">{{ formatFlextimeDuration(flextimeCredit) }}</div>
                </div>
                <div>
                    <div class="g4--text caption">{{ $t('Vacation available') }}</div>
                    <div class="headline">{{ calculatedVacationCredit }} {{ $t('days') }}</div>
                </div>
                <div v-if="canEditCompany">
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <icon-button
                                :on="on"
                                icon="Pencil"
                                @click="isFlextimeBalanceModalVisible = true"
                            />
                        </template>
                        {{ $t('Edit balance') }}
                    </v-tooltip>
                </div>
                <div>
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <icon-button
                                :on="on"
                                icon="CalendarClock"
                                @click="isFlextimeHistoryModalVisible = true"
                            />
                        </template>
                        {{ $t('View history') }}
                    </v-tooltip>
                </div>
                <div class="ml-10">
                    <primary-button @click=" isModalVisible=true">
                        {{ $t('Request') }}
                    </primary-button>
                </div>
            </div>
        </div>
        <data-table
            :items="items"
            :headers="headers"
            :loading="loader.isLoading"
            :options="tableOptions"
            row-clickable
            @update:options="tableOptions = $event"
            @click:row="onOpenModal"
        >
            <template v-slot:item.date_from="{ item }">
                {{ formatDatesFromUntil(parseSysDateTime(item.date_from), parseSysDateTime(item.date_until)) }}
            </template>
            <template v-slot:item.duration="{ item }">
                {{ formatDaysOrTimeFromDates(parseSysDateTime(item.date_from), parseSysDateTime(item.date_until)) }}
            </template>
            <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(parseSysDateTime(item.created_at)) }}
            </template>
            <template v-slot:item.status="{ item }">
                <div :class="formatStatusColor(item.status)">
                    {{ formatStatusText(item.status) }}
                </div>
            </template>
            <template v-slot:item.actionMenu="{ item }">
                <action-menu
                    :items="getActionItems(item)"
                    @action="getActionItems(item)[$event].action(item)"
                />
            </template>
        </data-table>
        <FlextimeVacationModal
            v-if="isModalVisible"
            :company-id="selectedCompanyId"
            :person-id="personId"
            :person-name="personName"
            :company-name="selectedCompanyName"
            :flextime-credit="flextimeCredit"
            :vacation-credit="vacationCredit"
            :calendar="selectedCalendar"
            @close="closeVacationModal"
            @update="fetchData"
        />
        <FlextimeHistoryModal
            v-if="isFlextimeHistoryModalVisible"
            :person-id="personId"
            :person-name="personName"
            :company-id="selectedCompanyId"
            @close="isFlextimeHistoryModalVisible = false"
        />
        <FlextimeBalanceModal
            v-if="isFlextimeBalanceModalVisible"
            :person-id="personId"
            :company-id="selectedCompanyId"
            :flextime-credit="flextimeCredit"
            :vacation-credit="vacationCredit"
            @close="isFlextimeBalanceModalVisible = false"
            @update="fetchData"
        />
    </div>
</template>

<script>
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import {$t} from "@/i18n";
import AccountModule from "@/store/modules/account";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import auth from "@/modules/auth/auth";
import Loader from "@/utility/loader";
import ApiCalendar from "@/api/calendar/calendar";
import {
    parseSysDateTime,
    formatDate,
    formatTimeDuration,
    formatDateTime,
    formatFlextimeDuration,
    formatDaysFromDuration,
    parseSysDate, parseTime, formatTime,
    formatDatesFromUntil,
    formatDaysOrTimeFromDates
} from "@/plugins/formatting";
import FlextimeVacationModal from "@/components/Flextime/FlextimeVacationModal.vue";
import {deepClone} from "@/helpers/helpers";
import CalendarEventModule, {CALENDAR_STATUS} from "@/modules/calendar/calendar";
import FlextimeBalanceModal from "@/views/Profile/Vacations/FlextimeBalanceModal";
import RightsModule from "@/composables/use-rights";
import {Router} from "@/composables/Router";
import FlextimeHistoryModal from "@/components/Flextime/FlextimeHistoryModal.vue";

export default {
    name: "ProfileVacations",
    components: {
        FlextimeHistoryModal,
        FlextimeVacationModal,
        FlextimeBalanceModal
    },
    props: {
        personId: {
            type: Number,
            default: 0
        },
        profile: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const router = Router();
        const {current_account} = AccountModule();
        const {can_edit_account} = RightsModule();
        const loader = reactive(new Loader());
        const selectedCalendar = ref(null);
        const isModalVisible = ref(false);
        const isFlextimeBalanceModalVisible = ref(false);
        const isFlextimeHistoryModalVisible = ref(false);

        const items = ref([]);
        const flextimeCredit = ref(0);
        const vacationCredit = ref(0);
        const tableOptions = ref({
            sortBy: ['date_from'],
            sortDesc: [true]
        })

        const flextimeCompanies = computed(() => props.profile.companies.filter(x => x.flextime_active))
        const selectedCompanyId = ref(flextimeCompanies.value.length ? flextimeCompanies.value[0].id : null);

        if (router.currentRoute.params.companyId) {
            selectedCompanyId.value = Number(router.currentRoute.params.companyId)
        }

        const selectedCompanyName = computed(() => {
            if (!selectedCompanyId.value) {
                return ""
            }

            return flextimeCompanies.value.find(x => x.id === selectedCompanyId.value).name
        })
        const canEditCompany = computed(() => can_edit_account(selectedCompanyId.value))


        const onOpenModal = (item) => {
            selectedCalendar.value = item
            isModalVisible.value = true
        }

        const getActionItems = (item) => {
            const isItemStatusEditable = !CalendarEventModule.LOCKED_CALENDAR_STATUSES.includes(item.status)
            const canEditByStatusAndRights = canEditCompany.value && isItemStatusEditable
            const canDelete = canEditCompany.value || !CalendarEventModule.LOCKED_CALENDAR_STATUSES.includes(item.status)

            let items = [
                {
                    text: $t("View"),
                    icon: 'Eye',
                    action: onOpenModal
                },
                canEditByStatusAndRights ? {
                    text: $t("Approve"),
                    icon: 'Check',
                    iconColor: 'tretiary',
                    action: async (item) => {
                        let itemCopy = deepClone(item)
                        itemCopy.status = CalendarEventModule.CALENDAR_STATUS.APPROVED

                        try {
                            await ApiCalendar.saveCalendarEvent(itemCopy.id, itemCopy)
                            await fetchData()
                        } catch (e) {
                            errorDialog.showError(e)
                        }
                    }
                } : null,
                canEditByStatusAndRights ? {
                    text: $t("Reject"),
                    icon: 'X',
                    iconColor: 'error',
                    action: async (item) => {
                        let itemCopy = deepClone(item)
                        itemCopy.status = CalendarEventModule.CALENDAR_STATUS.REJECTED

                        try {
                            await ApiCalendar.saveCalendarEvent(itemCopy.id, itemCopy)
                            await fetchData()
                        } catch (e) {
                            errorDialog.showError(e)
                        }
                    }
                } : null,
                canDelete ? {
                    text: $t("Delete"),
                    icon: 'Trash',
                    action: async (item) => {
                        try {
                            await ApiCalendar.deleteCalendarEvent(item.id)
                            await fetchData()
                        } catch (e) {
                            errorDialog.showError(e)
                        }
                    }
                } : null,
            ].filter(x => !!x)

            return items
        }

        const headers = computed(() => {
            let headers = [
                {
                    text: $t("Type"),
                    value: "type_translated",
                    sortable: true,
                },
                {
                    text: $t("Period"),
                    value: "date_from",
                    sortable: true,
                },
                {
                    text: $t("How long"),
                    value: "duration",
                    sortable: true,
                },
                {
                    text: $t("Requested"),
                    value: "created_at",
                    sortable: true,
                },
                {
                    text: $t("Status"),
                    value: "status",
                    sortable: true,
                },
                {
                    value: 'actionMenu',
                    sortable: false,
                    width: "0%"
                }
            ]

            return headers
        })

        const fetchData = async () => {
            try {
                const data = await ApiCalendar.getPersonVacations(selectedCompanyId.value, props.personId)
                items.value = data.calendars
                flextimeCredit.value = data.flextime_credit
                vacationCredit.value = data.vacation_credit
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        onMounted(() => {
            fetchData()
        })

        const onSelectCompanyAccount = (company) => {
            selectedCompanyId.value = company.id
            fetchData()
        }

        const closeVacationModal = () => {
            isModalVisible.value = false
            selectedCalendar.value = null
        }

        const formatStatusText = (status) => {
            if (status === CALENDAR_STATUS.APPROVED) {
                return $t('Approved')
            } else if (status === CALENDAR_STATUS.REJECTED) {
                return $t('Rejected')
            } else if (status === CALENDAR_STATUS.IN_REVIEW) {
                return $t('In review')
            }
        }

        const formatStatusColor = (status) => {
            if (status === CALENDAR_STATUS.APPROVED) {
                return 'tretiary--text'
            } else if (status === CALENDAR_STATUS.REJECTED) {
                return 'error--text'
            } else if (status === CALENDAR_STATUS.IN_REVIEW) {
                return 'primary--text'
            }
        }

        const getDuration = (item) => {
            if (!item.is_all_day) {
                const start = formatTime(item.date_from)
                const end = formatTime(item.date_until)

                return start + ' - ' + end
            } else {
                const start = parseSysDate(item.date_from)
                const end = parseSysDate(item.date_until)

                const diffInMs = end - start
                let diffInDays = diffInMs / (1000 * 60 * 60 * 24);
                diffInDays += 1
                diffInDays = Math.round(diffInDays)

                return diffInDays + ' ' + $t('Days')
            }
        }

        const calculatedVacationCredit = computed(() => {
            return vacationCredit.value ? vacationCredit.value / (60 * 60 * 24) : 0
        })

        const personName = computed(() => {
            return `${props.profile.person_settings.first_name ? props.profile.person_settings.first_name : ''} ${props.profile.person_settings.surname ? props.profile.person_settings.surname : ''}`
        })

        return {
            current_account,
            auth,
            headers,
            items,
            loader,
            parseSysDateTime,
            parseSysDate,
            formatDate,
            formatTimeDuration,
            formatDateTime,
            formatFlextimeDuration,
            flextimeCredit,
            vacationCredit,
            selectedCompanyId,
            onSelectCompanyAccount,
            selectedCalendar,
            fetchData,
            isModalVisible,
            closeVacationModal,
            formatStatusText,
            formatStatusColor,
            isFlextimeBalanceModalVisible,
            getActionItems,
            canEditCompany,
            formatDaysFromDuration,
            getDuration,
            onOpenModal,
            calculatedVacationCredit,
            flextimeCompanies,
            selectedCompanyName,
            formatDatesFromUntil,
            formatDaysOrTimeFromDates,
            isFlextimeHistoryModalVisible,
            personName,
            tableOptions
        };
    }
};
</script>
